import { CdkScrollable } from '@angular/cdk/scrolling';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { ControlPageFacade, ICatalogInfo, UserDataFacade } from '@ra-state';
import {
  IntegerDisplayType,
  MainButtonComponent,
  OutlinedButtonComponent,
  RaUiFormControl,
  SearchInputComponent,
  SpinnerInputComponent,
} from '@ra-web-tech-ui-toolkit/components';
import { debounceTime, map, startWith, switchMap } from 'rxjs';

// WTUI 17 removed minDigitsAfterPoint/maxDigitsAfterPoint; use IntegerDisplayType to keep spinner inputs whole-number only.
const integerDisplayType = new IntegerDisplayType();
const DEFAULT_SUBSCRIPTION_QUANTITY = 1;
const DEFAULT_DURATION = 365;
const DEFAULT_CREDIT_QUANTITY = 1000;
const MAX_CREDIT_QUANTITY = 1000;
const MAX_SUBSCRIPTION_QUANTITY = 200;
/** TO DO: reuse ContentModalComponent instead of this component */
@Component({
  selector: 'app-catalog-dialog',
  templateUrl: './catalog-dialog.component.html',
  styleUrls: ['./catalog-dialog.component.scss'],
  imports: [
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    SearchInputComponent,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    SpinnerInputComponent,
    MatSelectionList,
    MatListOption,
    MainButtonComponent,
    OutlinedButtonComponent,
    AsyncPipe,
  ],
  providers: [provideNativeDateAdapter()],
})
export class CatalogDialogComponent implements OnInit {
  data = inject<{
    header: string;
    body: string;
    cancelButtonText: string;
    OKButtonText: string;
    tenantId: string;
    cancelButtonVisible: boolean;
  }>(MAT_DIALOG_DATA);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly dialogRef = inject<MatDialogRef<CatalogDialogComponent>>(MatDialogRef);

  readonly integerDisplayType = integerDisplayType;

  @ViewChild('catSelection') catSelection: MatSelectionList;
  @ViewChild('qtyInput') qtyInput: SpinnerInputComponent;

  searchText = new FormControl();

  quantity = new RaUiFormControl(DEFAULT_SUBSCRIPTION_QUANTITY, [
    Validators.required,
    this.quantityValidator(1, MAX_SUBSCRIPTION_QUANTITY),
  ]);

  duration = new RaUiFormControl(DEFAULT_DURATION, [Validators.required, this.quantityValidator(1, 365)]);

  selectedCodes = new FormControl<ICatalogInfo[]>([], {
    validators: [Validators.required],
  });

  startDate = new Date(new Date().setDate(new Date().getDate() + 1)); // will set start date to current date + 1
  effectiveDate = new FormControl();

  formgroup = new FormGroup(
    [this.searchText, this.quantity, this.duration, this.selectedCodes, this.effectiveDate],
    this.formGroupValidator.bind(this),
  );

  catalogCodeList$ = this.controlPageFacade.mockableCatalogs$;

  errorMessages(min, maxVal): any {
    return {
      invalidQuantity: `This field is out of range [${min} - ${maxVal}]`,
      requiredQuantity: 'This field is required.',
      negativeQuantity: 'Please enter a positive value.',
      decimalQuantity: 'Please enter a whole number.',
    };
  }

  search$ = this.searchText.valueChanges.pipe(
    startWith(null),
    debounceTime(200),
    switchMap((res: string) => {
      if (!res) {
        return this.catalogCodeList$;
      }
      res = res.toLowerCase();
      return this.catalogCodeList$.pipe(
        map((codes) =>
          codes.filter(
            (it) =>
              it.catalogCode.toLowerCase().includes(res) ||
              it.name.toLowerCase().includes(res) ||
              it.appName.toLowerCase().includes(res),
          ),
        ),
      );
    }),
  );

  /// handle each selection change individually
  selectionChange(options: any): void {
    const current = options[0];
    let prevSelected = this.selectedCodes.value || [];
    if (current.selected) {
      prevSelected.push(current.value);
    } else {
      prevSelected = prevSelected.filter((it) => it !== current.value);
    }
    this.selectedCodes.setValue(prevSelected);
  }

  ngOnInit(): void {
    this.selectedCodes.setValue([]);
  }

  cancel(): void {
    this.close(false);
  }

  close(value): void {
    this.dialogRef.close(value);
  }

  addEntitlements(): void {
    const selectedCatalogCodes = this.selectedCodes.value || [];
    const quantity = this.quantity.value?.valueOf();
    const duration = this.duration.value?.valueOf();
    const rawDate: Date | null = this.effectiveDate.value;
    const effectiveDate = new DatePipe('en-US').transform(rawDate, 'yyyy-MM-dd') ?? '';
    this.userDataFacade.createSelectedFakeEntitlements(selectedCatalogCodes, quantity, duration, effectiveDate);
    this.close(true);
  }

  quantityValidator(minValue: number, maxValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }
      if (control.value < minValue || control.value > maxValue) {
        return { invalidQuantity: { value: control.value } };
      } else if (control.value % 1 !== 0) {
        return { decimalQuantity: { value: control.value } };
      }
      return null;
    };
  }
  /// for only credits, max is upto 1000
  /// for anything else (or a combo of credits and other codes), max is 200
  /// setting config for controls is finicky - does get into weird states once in a while.
  formGroupValidator(_control: AbstractControl): ValidationErrors | null {
    if (!this.selectedCodes.value) {
      return { required: { value: this.selectedCodes.value } };
    }
    const isOnlyCreditsSelected = this.selectedCodes.value.length === 1 && this.selectedCodes.value[0].isCredit;
    const maxValue = isOnlyCreditsSelected ? MAX_CREDIT_QUANTITY : MAX_SUBSCRIPTION_QUANTITY;
    this.quantity.setValidators([Validators.required, this.quantityValidator(1, maxValue)]);

    if (isOnlyCreditsSelected && this.quantity.value === DEFAULT_SUBSCRIPTION_QUANTITY) {
      this.quantity.setValue(DEFAULT_CREDIT_QUANTITY);
    } else if (!isOnlyCreditsSelected && this.quantity.value > MAX_SUBSCRIPTION_QUANTITY) {
      this.quantity.setValue(DEFAULT_SUBSCRIPTION_QUANTITY);
    }
    if (this.qtyInput && this.qtyInput.config.max !== maxValue) {
      this.qtyInput.config = {
        ...this.qtyInput.config,
        max: maxValue,
      };
      this.qtyInput.errorMessages = this.errorMessages(1, maxValue);
    }
    return null;
  }
}
